/**
 * Combat HUD.
 *
 * Built once, updated every frame, and every write goes through a dirty-check
 * binding. The UI budget is 0.5 ms/frame (docs/01 §9) and a naive
 * `innerHTML = …` rebuild of even this small tree blows it on its own — so the
 * only DOM this file touches per frame is a handful of textContent and
 * custom-property writes that actually changed.
 *
 * Layout keeps the centre of the screen empty. The HUD is chrome; the game is
 * the picture (docs/01 §6.4). *Where* the two blocks go is not decided here any
 * more — they claim `top-left` and `bottom-band` from `hudLayout.ts` and the
 * region layer places them, which is what stops the next overlay landing on
 * top of them (#52).
 */

import { claimRegion } from './hudLayout';
import {
  AttrBind,
  ClassBind,
  el,
  FillBind,
  NumBind,
  Pool,
  TextBind,
} from './style';
import type { AbilityVM, GatherVM, HandVM, PlayerVM, RhythmVM, StatusVM } from './viewModels';

const RHYTHM_SEGMENTS = 12;
const MAX_STATUS_CHIPS = 8;

/**
 * What `AbilityVM.keyHint` carries for an ability with no key at all — Cling,
 * which happens on wall contact. Distinct from an unresolved binding on
 * purpose: the tooltip says the name alone rather than naming a button.
 */
const NO_KEY = '—';

/** Hold tiers in `combat/gather.ts`'s ladder — the pip row has to be countable. */
const GATHER_PIPS = 4;

/** How many chevrons the hazard warning can show. Countable, so: few. */
const HAZARD_BANDS = 3;

function bar(colorVar: string, cls = 'bar'): { root: HTMLElement; fill: FillBind } {
  const root = el('div', cls);
  const i = el('i');
  i.style.setProperty('--c', colorVar);
  root.appendChild(i);
  return { root, fill: new FillBind(i) };
}

/** One hand card: weapon identity, combo position, recovery, charge/heat/ammo. */
class HandCard {
  readonly root: HTMLElement;
  private name = new TextBind(el('div'));
  private key: TextBind;
  private glyph: TextBind;
  private sub: TextBind;
  private armed: AttrBind;
  private rarity: AttrBind;
  private ready: ClassBind;
  private recovery: FillBind;
  private charge: FillBind;
  private chargeRow: AttrBind;
  private heat: FillBind;
  private heatRow: AttrBind;
  private reload: FillBind;
  private reloadRow: AttrBind;
  private reserve: FillBind;
  private reserveRow: AttrBind;
  private reserveKind: AttrBind;
  private overheated: AttrBind;
  private pips: HTMLElement[] = [];
  private pipOn: number[] = [];
  private pipHost: HTMLElement;
  private lastComboLen = -1;

  constructor(hand: 'main' | 'off') {
    this.root = el('div', 'hand');
    this.root.dataset.hand = hand;

    // Blank until the first update, for the same reason the ability strip's cap
    // is (#134): the label is resolved from the live binding table, which lives
    // on the far side of the view model. A panel that spelled a key here could
    // only ever spell the wrong one for a player who moved it.
    const key = el('div', 'hand__key', '');
    this.key = new TextBind(key);
    const g = el('div', 'hand__glyph', '·');
    this.glyph = new TextBind(g);

    const body = el('div', 'hand__body');
    this.name = new TextBind(el('div', 'hand__name ui-rar', '—'));
    this.rarity = new AttrBind(this.name.el, 'data-rarity');
    this.pipHost = el('div', 'hand__combo');
    const sub = el('div', 'hand__sub');
    this.sub = new TextBind(sub);
    body.append(this.name.el, this.pipHost, sub);

    const bars = el('div', 'hand__bars');
    const rec = bar('var(--ui-accent)');
    const chg = bar('var(--ui-accent-2)');
    const hot = bar('var(--ui-bad)');
    // Reload and reservoir are their own bars rather than sharing one: they can
    // be live at the same time (a spin-up weapon reloading), and a bar that
    // means two things is a bar that means nothing.
    const rel = bar('var(--ui-warn)', 'bar hand__reload');
    const rsv = bar('var(--ui-accent-2)', 'bar hand__reserve');
    this.recovery = rec.fill;
    this.charge = chg.fill;
    // `data-off`, not `hidden`. The four optional bars share a centred column
    // with the recovery bar, so a row that leaves the flow re-centres the one
    // readout the player is actually watching — see the note on
    // `.hand__bars > .bar[data-off="1"]` in style.ts (#128).
    this.chargeRow = new AttrBind(chg.root, 'data-off');
    this.heat = hot.fill;
    this.heatRow = new AttrBind(hot.root, 'data-off');
    this.reload = rel.fill;
    this.reloadRow = new AttrBind(rel.root, 'data-off');
    this.reserve = rsv.fill;
    this.reserveRow = new AttrBind(rsv.root, 'data-off');
    this.reserveKind = new AttrBind(rsv.root, 'data-kind');
    bars.append(rec.root, chg.root, rsv.root, hot.root, rel.root);

    this.root.append(key, g, body, bars);
    this.armed = new AttrBind(this.root, 'data-armed');
    this.overheated = new AttrBind(this.root, 'data-overheated');
    this.ready = new ClassBind(this.root, 'is-ready');
  }

  update(h: HandVM): void {
    this.key.set(h.keyHint);
    this.glyph.set(h.weaponId ? h.glyph : '·');
    this.name.set(h.weaponId ? h.name : 'EMPTY');
    this.rarity.set(h.rarity);
    this.recovery.set(h.ready01);
    this.armed.set(h.detonationArmed ? '1' : '0');
    this.ready.set(h.ready);

    // Combo pips: only rebuild the pool when the combo length changes, which is
    // once per weapon swap rather than once per frame.
    if (h.comboLength !== this.lastComboLen) {
      this.lastComboLen = h.comboLength;
      while (this.pips.length < h.comboLength) {
        const p = el('i');
        this.pips.push(p);
        this.pipOn.push(-1);
        this.pipHost.appendChild(p);
      }
      // Spare pips go blank rather than absent, like every other optional part
      // of this card (#128). Nothing inside a hand card is allowed to leave the
      // flow, so that the one rule the card has to obey has no exceptions to
      // remember — see tests/handCardBox.test.ts.
      for (let i = 0; i < this.pips.length; i++) {
        const spare = i < h.comboLength ? '0' : '1';
        if (this.pips[i].dataset.spare !== spare) this.pips[i].dataset.spare = spare;
      }
    }
    for (let i = 0; i < h.comboLength; i++) {
      const on = i <= h.comboStep ? 1 : 0;
      if (this.pipOn[i] !== on) {
        this.pipOn[i] = on;
        this.pips[i].dataset.on = on ? '1' : '0';
      }
    }

    const hasCharge = h.charge01 !== null;
    this.chargeRow.set(hasCharge ? '0' : '1');
    if (hasCharge) this.charge.set(h.charge01!);

    const hasHeat = h.heat01 !== null;
    this.heatRow.set(hasHeat ? '0' : '1');
    if (hasHeat) this.heat.set(h.heat01!);
    this.overheated.set(h.overheated ? '1' : '0');

    // The reload bar still *draws* only while reloading — a permanently-full
    // bar on every magazine weapon is three pixels of chrome that never says
    // anything — but it keeps its slot, so nothing moves when it starts.
    this.reloadRow.set(h.reloading ? '0' : '1');
    if (h.reloading) this.reload.set(h.reload01);

    const hasReserve = h.reserve01 !== null;
    this.reserveRow.set(hasReserve ? '0' : '1');
    if (hasReserve) {
      this.reserve.set(h.reserve01!);
      this.reserveKind.set(h.reserveKind);
    }

    this.sub.set(handSubtitle(h));
  }
}

/**
 * The sub-line is one string so it is one comparison and at most one write.
 * Building it allocates only when something in it changed — TextBind bails out
 * before touching the DOM, and the string itself is short-lived nursery
 * garbage that never escapes the frame.
 */
function handSubtitle(h: HandVM): string {
  if (!h.weaponId) return '';
  let s = h.comboNodeName;
  if (h.ammoMax !== null) s += '  ' + h.ammo + '/' + h.ammoMax;
  // The reservoir is named, not just filled: a spin-up you have to keep alive
  // and a fuel tank you have to refill are opposite instructions, and the bar
  // alone cannot tell them apart.
  if (h.reserveKind) s += '  ' + h.reserveKind.toUpperCase();
  if (h.reloading) s += '  RELOADING';
  else if (h.overheated) s += '  ⚠OVERHEATED';
  if (h.detonationArmed) s += '  ▲DETONATE';
  return s;
}

export class Hud {
  /**
   * The two blocks this class owns, in two different regions. There is no single
   * root any more, and there deliberately is not: a wrapper spanning both would
   * have to be positioned, and positioning is what the region layer took away.
   */
  private blocks: HTMLElement[] = [];

  private hpText: NumBind;
  private hpFill: FillBind;
  private hpMaxText: NumBind;
  private shieldFill: FillBind;
  private shieldRow: HTMLElement;
  /** Issue #171. Flight/hover-thrust fuel — hidden entirely for a `'double-jump'` body. */
  private flightFill: FillBind;
  private flightRow: HTMLElement;

  private statusHost: HTMLElement;
  private statusPool: Pool<HTMLElement>;
  private statusIds: string[] = [];
  private statusOverflow: TextBind;
  private scrip: NumBind;
  private ash: NumBind;

  private dashPool: Pool<HTMLElement>;
  private abilityPool: Pool<HTMLElement>;
  private abilityIds: string[] = [];
  private abilityKey: TextBind;
  private abilityMore: TextBind;

  private gatherRoot: AttrBind;
  private gatherPips: HTMLElement[] = [];
  private gatherTier = -2;
  private gatherHazard: TextBind;
  private gatherNear: AttrBind;

  private main: HandCard;
  private off: HandCard;

  private rhythmRoot: HTMLElement;
  private rhythmSegs: HTMLElement[] = [];
  private rhythmOn = -1;
  private rhythmDecay: AttrBind;
  private rhythmTier: TextBind;
  private rhythmNext: TextBind;
  private rhythmBonus: TextBind;

  constructor(host: HTMLElement) {
    // ---- top-left: vitals + statuses
    const tl = el('div', 'hud__c hud__c--tl');
    const vit = el('div', 'hud__vit');
    const hpline = el('div', 'hud__hpline');
    const hpNum = el('span', '', '0');
    const hpMax = el('span', 'ui-mute', '/0');
    hpline.append(el('span', 'ui-dim', 'VITALS'), el('span', '', ''));
    const hpVals = hpline.lastElementChild as HTMLElement;
    hpVals.append(hpNum, hpMax);
    this.hpText = new NumBind(hpNum, (n) => String(Math.ceil(n)));
    this.hpMaxText = new NumBind(hpMax, (n) => '/' + Math.ceil(n));

    const hp = bar('var(--ui-hp)', 'bar hud__hp');
    const sh = bar('var(--ui-shield)', 'bar hud__shield');
    const fl = bar('var(--ui-flight)', 'bar hud__flight');
    this.hpFill = hp.fill;
    this.shieldFill = sh.fill;
    this.shieldRow = sh.root;
    this.flightFill = fl.fill;
    this.flightRow = fl.root;
    vit.append(hpline, hp.root, sh.root, fl.root);

    this.statusHost = el('div', 'hud__statuses');
    this.statusPool = new Pool(this.statusHost, () => {
      const s = el('div', 'status');
      s.append(el('span'), el('b'), el('u'));
      return s;
    });
    const overflow = el('div', 'ui-mute');
    this.statusOverflow = new TextBind(overflow);

    // The run purse. Scrip and Ash sit together because the *contrast* is the
    // information: scrip is spent inside this run and dies with you, ash banks
    // forever (docs/05 §4). Shown alone, a balance teaches neither.
    const purse = el('div', 'hud__purse');
    const scripEl = el('span', 'purse purse--scrip');
    scripEl.title = 'Scrip — spent inside this run. Lost when you die.';
    const ashEl = el('span', 'purse purse--ash');
    ashEl.title = 'Ash — banked between runs. Survives death.';
    this.scrip = new NumBind(scripEl, (n) => '⌾ ' + Math.floor(n));
    this.ash = new NumBind(ashEl, (n) => '🜃 ' + Math.floor(n));
    purse.append(scripEl, ashEl);

    tl.append(vit, purse, this.statusHost, overflow);

    // ---- bottom-left: main hand, dash charges, gathering field, abilities
    const bl = el('div', 'hud__c hud__c--bl');

    // The ability strip. One key cap for the whole row, then one glyph per
    // ability the run has actually found, then a count of the ones it has not
    // — see `updateAbilities` for why that is the whole of it.
    const abilityRow = el('div', 'hud__abilities');
    // Blank until the first update: the label is resolved from the binding
    // table, which lives on the far side of the view model (see
    // `PlayerVM.abilityKey`), so this panel never spells a key itself.
    const abilityKey = el('div', 'ability__key', '');
    abilityKey.title = 'Traversal — one button, the context picks which';
    this.abilityKey = new TextBind(abilityKey);
    const abilityHost = el('div', 'hud__abilityglyphs');
    this.abilityPool = new Pool(abilityHost, () => {
      const a = el('div', 'ability');
      // glyph · charges · cooldown wipe. Three elements, all present always:
      // an ability with no charge economy blanks the middle one rather than
      // dropping it, for the reason #128 spells out.
      a.append(el('span'), el('b'), el('u'));
      return a;
    });
    const abilityMore = el('div', 'ability__more');
    this.abilityMore = new TextBind(abilityMore);
    abilityRow.append(abilityKey, abilityHost, abilityMore);

    // The gathering field (#122's readings, #129's space). Countable pips for
    // the hold tier, and a named hazard warning that is the safety half.
    const gather = el('div', 'gather');
    this.gatherRoot = new AttrBind(gather, 'data-on');
    const gatherPips = el('div', 'gather__tier');
    for (let i = 0; i < GATHER_PIPS; i++) {
      const p = el('i');
      this.gatherPips.push(p);
      gatherPips.appendChild(p);
    }
    const hazard = el('div', 'gather__hazard');
    this.gatherHazard = new TextBind(hazard);
    this.gatherNear = new AttrBind(hazard, 'data-near');
    gather.append(el('div', 'gather__glyph', '⊙'), gatherPips, hazard);

    const dashHost = el('div', 'hud__dash');
    this.dashPool = new Pool(dashHost, () => {
      const p = el('div', 'dashpip');
      p.appendChild(el('i'));
      return p;
    });
    this.main = new HandCard('main');
    bl.append(abilityRow, gather, dashHost, this.main.root);

    // ---- bottom-right: off hand
    const br = el('div', 'hud__c hud__c--br');
    this.off = new HandCard('off');
    br.append(this.off.root);

    // ---- bottom-centre: rhythm meter (docs/04 §8)
    const bc = el('div', 'hud__c hud__c--bc');
    this.rhythmRoot = el('div', 'rhythm');
    const segs = el('div', 'rhythm__segs');
    for (let i = 0; i < RHYTHM_SEGMENTS; i++) {
      const s = el('i');
      this.rhythmSegs.push(s);
      segs.appendChild(s);
    }
    const meta = el('div', 'rhythm__meta');
    const tier = el('span', '', 'RHYTHM');
    const next = el('span', 'rhythm__next', '');
    const bonus = el('span', '', '');
    meta.append(tier, next, bonus);
    this.rhythmTier = new TextBind(tier);
    this.rhythmNext = new TextBind(next);
    this.rhythmBonus = new TextBind(bonus);
    this.rhythmDecay = new AttrBind(this.rhythmRoot, 'data-decay');
    this.rhythmRoot.append(segs, meta);
    bc.append(this.rhythmRoot);

    // The three bottom stacks share one flex row rather than each pinning
    // itself to a corner. Pinned independently they need ~700px combined and
    // silently overlap into an unreadable pile below that — which is the one
    // failure the clarity budget (docs/01 §6.4) is not allowed to have. In a
    // flex row they cannot overlap at any width; they compress instead.
    const bottom = el('div', 'hud__bottom');
    bottom.append(bl, bc, br);

    claimRegion(host, 'top-left').appendChild(tl);
    claimRegion(host, 'bottom-band').appendChild(bottom);
    this.blocks = [tl, bottom];
  }

  update(p: PlayerVM): void {
    this.hpText.set(p.hp);
    this.hpMaxText.set(p.hpMax);
    this.hpFill.set(p.hpMax > 0 ? p.hp / p.hpMax : 0);

    const hasShield = p.shieldMax > 0;
    if (this.shieldRow.hidden === hasShield) this.shieldRow.hidden = !hasShield;
    if (hasShield) this.shieldFill.set(p.shield / p.shieldMax);

    if (this.flightRow.hidden === p.hasFlightMeter) this.flightRow.hidden = !p.hasFlightMeter;
    if (p.hasFlightMeter) this.flightFill.set(p.flightFuel01);

    this.scrip.set(p.scrip);
    this.ash.set(p.ash);

    this.updateStatuses(p.statuses);
    this.updateDash(p);
    this.updateAbilities(p.abilities, p.abilityKey);
    this.updateGather(p.gather);

    this.main.update(p.main);
    this.off.update(p.off);
    this.updateRhythm(p.rhythm);
  }

  /**
   * Statuses are glyph boxes, never colour washes — three at once must stay
   * individually countable (docs/01 §6.4). The identity check is per-index
   * against a cached id array so an unchanged status list costs N comparisons
   * and zero allocation.
   */
  private updateStatuses(list: StatusVM[]): void {
    const n = Math.min(list.length, MAX_STATUS_CHIPS);
    this.statusPool.begin();
    for (let i = 0; i < n; i++) {
      const s = list[i];
      const box = this.statusPool.next();
      if (this.statusIds[i] !== s.id) {
        this.statusIds[i] = s.id;
        (box.firstChild as HTMLElement).textContent = s.glyph;
        box.dataset.kind = s.kind;
        box.title = s.desc ? s.name + ' — ' + s.desc : s.name;
      }
      const stackEl = box.children[1] as HTMLElement;
      const stackText = s.stacks > 1 ? String(s.stacks) : '';
      if (stackEl.textContent !== stackText) stackEl.textContent = stackText;
      const t = (box.children[2] as HTMLElement).style;
      const q = Math.round(Math.max(0, Math.min(1, s.remaining01)) * 40) / 40;
      if (t.getPropertyValue('--f') !== String(q)) t.setProperty('--f', String(q));
    }
    this.statusPool.end();
    this.statusIds.length = n;
    this.statusOverflow.set(list.length > n ? '+' + (list.length - n) + ' more' : '');
  }

  private updateDash(p: PlayerVM): void {
    this.dashPool.begin();
    for (let i = 0; i < p.dashMax; i++) {
      const pip = this.dashPool.next();
      const full = i < p.dashCharges ? '1' : '0';
      if (pip.dataset.full !== full) pip.dataset.full = full;
      // Only the charge immediately after the last full one is regenerating.
      const f = i === p.dashCharges ? p.dashRecharge01 : 0;
      const q = Math.round(Math.max(0, Math.min(1, f)) * 20) / 20;
      const st = (pip.firstChild as HTMLElement).style;
      if (st.getPropertyValue('--f') !== String(q)) st.setProperty('--f', String(q));
    }
    this.dashPool.end();
  }

  /**
   * Traversal abilities — glyphs the run has found, not rows it might one day.
   *
   * #129. This used to be nine labelled chips: glyph, full name and a key hint,
   * for every ability in the game, unfound ones dimmed. That strip sat directly
   * above the main-hand card, in the position the player watches during a
   * fight, and it was carrying very little. Three arguments cut it down:
   *
   *   - **The names are not news.** An ability's name is a constant you learn
   *     once. What changes mid-fight is four transient facts — found, armed,
   *     ready, cooling — and docs/01 §6.4 already decided the form for exactly
   *     that shape of information: countable glyphs, distinguishable by *shape*,
   *     never a colour wash. `updateStatuses` above is the same rule applied to
   *     the same problem. An ability that is found-but-not-ready wants a glyph
   *     state, not a row.
   *   - **The key was printed nine times and had three values.** Seven of the
   *     nine are the *same button* and the context picks between them (docs/02
   *     §6.5). One cap for the row says that; nine caps saying `L` says it nine
   *     times and implies, wrongly, that they are nine separate controls. The
   *     two that differ carry their own hint in the tooltip.
   *   - **Most of the row was not the player's.** Innate abilities exist on
   *     three of seventeen archetypes and attunement slots are zero on a fresh
   *     profile, so a first run had eight or nine of nine chips dimmed. The
   *     promise of what is out there to find — the Metroidvania engine the old
   *     comment here rightly defended — is *one* fact, not eight rows of it, and
   *     it survives as the `+N` at the end of the row.
   *
   * What is deliberately *not* done: hover-to-label, and folding the row into
   * the Tab radial. Both fail the same check. The radial is a weapon quick-swap
   * wheel opened by a Tab-hold timer in `uiRoot.bindKeys` and steered with the
   * mouse, and the pad path #78 added (`UIRoot.pumpPad`) returns early while no
   * panel is open — which is the only state the radial opens in. So a pad
   * player cannot reach it at all, and a pad has no hover either. Putting
   * readiness behind either one hides it from a whole input device.
   */
  private updateAbilities(list: AbilityVM[], rowKey: string): void {
    this.abilityPool.begin();
    let shown = 0;
    let unfound = 0;
    for (let i = 0; i < list.length; i++) {
      const a = list[i];
      if (!a.unlocked) {
        unfound++;
        continue;
      }
      const chip = this.abilityPool.next();
      if (this.abilityIds[shown] !== a.id) {
        this.abilityIds[shown] = a.id;
        (chip.children[0] as HTMLElement).textContent = a.glyph;
      }
      // The name and the key live in the tooltip rather than the strip. It is
      // reference text — read once, on purpose — and the row is not the place
      // to keep answering a question nobody is asking mid-fight.
      //
      // Compared against what is on the element rather than cached against the
      // ability id (#134). The id is the only thing about a chip that cannot
      // change while it is on screen, and the key hint is the one thing in the
      // tooltip that can: a player who rebinds `ability` mid-run kept the old
      // letter here for the rest of the run, because the slot still held the
      // same ability and the cache said nothing had happened. One string
      // comparison across at most nine chips, the same shape as the charge
      // count below.
      const title = a.keyHint === NO_KEY ? a.name : a.name + ' — ' + a.keyHint;
      if (chip.title !== title) chip.title = title;
      const ready = a.ready ? '1' : '0';
      if (chip.dataset.ready !== ready) chip.dataset.ready = ready;
      const armed = a.armed ? '1' : '0';
      if (chip.dataset.armed !== armed) chip.dataset.armed = armed;

      // Charges are the count itself, not "2/2": at this size the maximum is
      // the same constant every frame and only the numerator is news.
      const charge = chip.children[1] as HTMLElement;
      const chargeText = a.charges === null ? '' : String(a.charges);
      if (charge.textContent !== chargeText) charge.textContent = chargeText;

      // The cooldown wipe is quantised so a bar that is visually identical does
      // not write a custom property every frame.
      const cd = (chip.children[2] as HTMLElement).style;
      const q = Math.round(Math.max(0, Math.min(1, a.cooldown01)) * 20) / 20;
      if (cd.getPropertyValue('--f') !== String(q)) cd.setProperty('--f', String(q));
      shown++;
    }
    this.abilityPool.end();
    this.abilityIds.length = shown;
    // The cap goes blank rather than absent when the run has found nothing yet
    // — the row keeps its height either way (#128).
    this.abilityKey.set(shown > 0 ? rowKey : '');
    this.abilityMore.set(unfound > 0 ? '+' + unfound : '');
  }

  /**
   * The gathering field (#122's readings, finally read).
   *
   * Two facts, both transient, both glyphs. The tier is a countable pip ladder
   * — how far up the hold you are, against a total you can see — and the hazard
   * is a name plus a proximity attribute the sheet turns into a countable
   * chevron run. Neither is a colour wash and neither is a bar, for the reason
   * docs/01 §6.4 gives and the ability strip above obeys.
   *
   * The hazard half is a **safety** affordance rather than a decoration: the
   * field pulls lava, and the whole argument that this is fair rests on the
   * player getting the 650 ms of notice the field actually gives them. A
   * warning that only reaches the run log is not notice.
   *
   * The block keeps its footprint when the field is shut (#128): `data-on`
   * dims it, and the pips and the hazard line are always in the flow, so
   * opening a field does not move the hand card underneath it.
   */
  private updateGather(g: GatherVM): void {
    this.gatherRoot.set(g.active ? '1' : '0');

    const tier = g.active ? g.tier : -1;
    if (tier !== this.gatherTier) {
      this.gatherTier = tier;
      for (let i = 0; i < this.gatherPips.length; i++) {
        this.gatherPips[i].dataset.on = i <= tier ? '1' : '0';
      }
    }

    // A count, not a plural: "lava x7" is a different instruction from "lava",
    // and the field reports the number for free.
    const many = g.hazardCount > 1 ? ' ×' + g.hazardCount : '';
    this.gatherHazard.set(g.hazard ? '⚠ ' + g.hazard + many : '');
    // Quantised to the chevron count the sheet draws, so a hazard drifting
    // inside one band does not rewrite the attribute sixty times a second.
    const near = g.hazard
      ? String(Math.min(HAZARD_BANDS, 1 + Math.floor(Math.max(0, Math.min(0.999, g.hazardClose01)) * HAZARD_BANDS)))
      : '0';
    this.gatherNear.set(near);
  }

  private updateRhythm(r: RhythmVM): void {
    const on = Math.round(Math.max(0, Math.min(1, r.value01)) * RHYTHM_SEGMENTS);
    if (on !== this.rhythmOn) {
      // Only touch the segments that crossed the threshold.
      const from = Math.min(on, this.rhythmOn < 0 ? 0 : this.rhythmOn);
      const to = Math.max(on, this.rhythmOn < 0 ? RHYTHM_SEGMENTS : this.rhythmOn);
      for (let i = from; i < to; i++) {
        this.rhythmSegs[i].dataset.on = i < on ? '1' : '0';
      }
      this.rhythmOn = on;
    }
    this.rhythmDecay.set(r.decaying ? '1' : '0');
    this.rhythmTier.set('RHYTHM ' + r.tier + '/' + r.tierMax);
    this.rhythmNext.set(r.nextHand ? (r.nextHand === 'main' ? '◀ MAIN' : 'OFF ▶') : '');
    this.rhythmBonus.set(r.bonusText);
  }

  /**
   * Take the HUD off the screen without tearing it down — the prologue's one
   * requirement (docs/08 §2). It used to be `hudLayer.hidden = true`; the two
   * blocks are in two shared regions now, so it has to be per-block, and hiding
   * the region layer instead would take the run log and the prompts with it.
   */
  setHidden(on: boolean): void {
    for (const b of this.blocks) if (b.hidden !== on) b.hidden = on;
  }

  destroy(): void {
    this.statusPool.dispose();
    this.dashPool.dispose();
    this.abilityPool.dispose();
    for (const b of this.blocks) b.remove();
    this.blocks.length = 0;
  }
}
